import { createFileRoute } from "@tanstack/react-router";
import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import { z } from "zod";
import { Button } from "@/components/ui/button";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import {
  Share2,
  ShieldCheck,
  Briefcase,
  MessageSquare,
  CheckCircle,
} from "lucide-react";
import { PhoneInput } from "@/components/Inputs/PhoneInput";
import { useState } from "react";
import { useMutation, useQuery } from "@tanstack/react-query";
import { useTRPC } from "@/trpc";

export const Route = createFileRoute("/_org/org/create/")({
  component: RouteComponent,
});

const formSchema = z.object({
  name: z.string().min(2, {
    message: "Organization name must be at least 2 characters.",
  }),
  contactName: z.string().min(2, {
    message: "Your name must be at least 2 characters.",
  }),
  phoneNumber: z.string().min(10, {
    message: "Please enter a valid phone number.",
  }),
  role: z.string({
    required_error: "Please select a role.",
  }),
});

function RouteComponent() {
  const trpc = useTRPC();
  const [isSubmitted, setIsSubmitted] = useState(false);

  const createRequestMutation = useMutation(
    trpc.organization.createOrganizationCreateRequest.mutationOptions(),
  );
  const existingRequestQuery = useQuery(
    trpc.organization.getOrganizationCreateRequest.queryOptions(),
  );

  const form = useForm<z.infer<typeof formSchema>>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      name: "",
      contactName: "",
      phoneNumber: "",
      role: "",
    },
  });

  async function onSubmit(values: z.infer<typeof formSchema>) {
    try {
      await createRequestMutation.mutateAsync(values);
      setIsSubmitted(true);
      existingRequestQuery.refetch();
    } catch (error) {
      console.error("Failed to submit organization request:", error);
    }
  }

  const renderFeatureCards = () => (
    <div className="space-y-4 md:space-y-6">
      <Card>
        <CardHeader className="flex flex-row items-center gap-4 p-4 md:p-6">
          <Briefcase className="h-5 w-5 md:h-6 md:w-6 text-primary flex-shrink-0" />
          <div>
            <CardTitle className="text-base md:text-lg">
              Job Posts & Hiring
            </CardTitle>
            <CardDescription>
              Post jobs, match interested providers, and run your hiring
              workflow
            </CardDescription>
          </div>
        </CardHeader>
      </Card>

      <Card>
        <CardHeader className="flex flex-row items-center gap-4 p-4 md:p-6">
          <ShieldCheck className="h-5 w-5 md:h-6 md:w-6 text-primary flex-shrink-0" />
          <div>
            <CardTitle className="text-base md:text-lg">
              Credential Management
            </CardTitle>
            <CardDescription>
              Track licenses, certifications, and expirations in one place
            </CardDescription>
          </div>
        </CardHeader>
      </Card>

      <Card>
        <CardHeader className="flex flex-row items-center gap-4 p-4 md:p-6">
          <MessageSquare className="h-5 w-5 md:h-6 md:w-6 text-primary flex-shrink-0" />
          <div>
            <CardTitle className="text-base md:text-lg">
              Chat & Offers
            </CardTitle>
            <CardDescription>
              Message providers and send offers or shifts directly
            </CardDescription>
          </div>
        </CardHeader>
      </Card>
      <Card>
        <CardHeader className="flex flex-row items-center gap-4 p-4 md:p-6">
          <Share2 className="h-5 w-5 md:h-6 md:w-6 text-primary flex-shrink-0" />
          <div>
            <CardTitle className="text-base md:text-lg">
              Provider Directory & Sharing
            </CardTitle>
            <CardDescription>
              Build your bench — receive shared profiles or add providers
              directly
            </CardDescription>
          </div>
        </CardHeader>
      </Card>
    </div>
  );

  const renderConfirmation = () => (
    <Card>
      <CardHeader className="p-4 md:p-6">
        <div className="flex items-center gap-3 mb-2">
          <CheckCircle className="h-6 w-6 text-green-500" />
          <CardTitle className="text-xl">Request Submitted</CardTitle>
        </div>
        <CardDescription>
          Your request has been submitted successfully. Our team will contact
          you shortly.
        </CardDescription>
      </CardHeader>
    </Card>
  );

  const renderForm = () => (
    <Card>
      <CardHeader className="p-4 md:p-6">
        <CardTitle className="text-xl">Organization Details</CardTitle>
        <CardDescription>
          Fill out the form below to get started with your organization.
        </CardDescription>
      </CardHeader>
      <CardContent className="p-4 md:p-6 pt-0">
        <Form {...form}>
          <form
            onSubmit={form.handleSubmit(onSubmit)}
            className="space-y-4 md:space-y-6"
          >
            <FormField
              control={form.control}
              name="name"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Organization Name</FormLabel>
                  <FormControl>
                    <Input placeholder="Enter organization name" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="contactName"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Your Name</FormLabel>
                  <FormControl>
                    <Input placeholder="Enter your full name" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="phoneNumber"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Phone Number</FormLabel>
                  <FormControl>
                    <PhoneInput
                      value={field.value}
                      onChange={field.onChange}
                      placeholder="555-555-5555"
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="role"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Your Role</FormLabel>
                  <Select
                    onValueChange={field.onChange}
                    defaultValue={field.value}
                  >
                    <FormControl>
                      <SelectTrigger>
                        <SelectValue placeholder="Select your role" />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      <SelectItem value="owner">Owner</SelectItem>
                      <SelectItem value="director">Director</SelectItem>
                      <SelectItem value="administrator">
                        Administrator
                      </SelectItem>
                      <SelectItem value="other">Other</SelectItem>
                    </SelectContent>
                  </Select>
                  <FormMessage />
                </FormItem>
              )}
            />

            <Button
              type="submit"
              className="w-full py-6"
              disabled={createRequestMutation.isPending}
            >
              {createRequestMutation.isPending
                ? "Submitting..."
                : "Create Organization"}
            </Button>
            <p className="text-center text-sm text-muted-foreground">
              Takes less than a minute — our team will reach out to finish
              setting up your account.
            </p>
          </form>
        </Form>
      </CardContent>
    </Card>
  );

  return (
    <div className="px-4 md:px-6 py-6 md:py-8 max-w-7xl mx-auto w-full">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 md:gap-8">
        <div>
          <h1 className="text-2xl md:text-3xl font-bold mb-4 md:mb-6">
            Find, hire, and manage providers in one place
          </h1>
          <p className="text-base md:text-lg mb-6">
            Saile helps healthcare facilities find providers, manage
            credentials, run hiring workflows, and pay providers — all from a
            single dashboard.
          </p>

          {renderFeatureCards()}
        </div>

        <div>
          {existingRequestQuery.isLoading ? (
            <Card className="p-6">
              <p className="text-center">Loading...</p>
            </Card>
          ) : existingRequestQuery.data || isSubmitted ? (
            renderConfirmation()
          ) : (
            renderForm()
          )}
        </div>
      </div>
    </div>
  );
}
